import { useCallback, useMemo } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";

import { getDisplayValue, getRawValue } from "../../lib/data-entry-format";
import { toDisplayDueDate } from "../../lib/due-date";
import type {
  CurrentFieldValues,
  DataRequestFieldResponse,
  DataRequestFreeTextQuestion,
  SaveFieldHandler,
} from "../../models/data-request.model";
import type { DataRequestFieldDefinition } from "../create-data-request-dialog/data-request-fields";
import { DataRequestDocumentsSection } from "../data-request-documents-section/data-request-documents-section";

import { DataEntryFieldRow } from "./data-entry-field-row";
import { FreeTextSection } from "./free-text-section";
import { IndicatorValuesField } from "./indicator-values-field";
import { useDateRangeDraft } from "./use-date-range-draft";
import { loadDraftMap } from "./use-draft-persistence";

interface FieldDataEntryProps {
  entityUrl: string | null;
  dueDate: string | undefined;
  fieldValues: CurrentFieldValues | null;
  visibleFieldList: DataRequestFieldDefinition[];
  fieldResponseMap: Record<string, DataRequestFieldResponse>;
  workspaceId: string;
  dataRequestEntityId: string;
  documentsRequested: boolean;
  freeTextQuestions: DataRequestFreeTextQuestion[];
  initialFreeTextAnswers: Record<string, string>;
  isReadOnly: boolean;
  submitting: boolean;
  onSaveField: SaveFieldHandler;
  onSaveFreeText: (answers: Record<string, string>) => void;
  onSubmit: () => void;
}

export function FieldDataEntry({
  entityUrl,
  dueDate,
  fieldValues,
  visibleFieldList,
  fieldResponseMap,
  workspaceId,
  dataRequestEntityId,
  documentsRequested,
  freeTextQuestions,
  initialFreeTextAnswers,
  isReadOnly,
  submitting,
  onSaveField,
  onSaveFreeText,
  onSubmit,
}: FieldDataEntryProps) {
  const { t, i18n } = useDomainTranslation("data-request");
  const navigate = useNavigate();

  const tableFieldList = visibleFieldList.filter((field) => field.fieldType !== "indicator-values");
  const showValuesEditor = visibleFieldList.some((field) => field.fieldType === "indicator-values");

  const draftMap = useMemo(() => loadDraftMap(dataRequestEntityId), [dataRequestEntityId]);

  const { maxSelectableStartDate, minSelectableEndDate, recordSave } = useDateRangeDraft({
    draftMap,
    fieldResponseMap,
    fieldValues,
  });

  const handleSaveField = useCallback<SaveFieldHandler>(
    (input) => {
      recordSave(input);
      onSaveField(input);
    },
    [recordSave, onSaveField],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1 h-3 w-3" />
            {t("dataEntry.goBack")}
          </Button>
          <span className="text-sm font-medium">{t("dataEntry.title")}</span>
        </div>
        {entityUrl && (
          <Button variant="ghost" size="sm" asChild>
            <a href={entityUrl} target="_blank" rel="noopener noreferrer">
              {t("dataEntry.openEntity")}
              <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 px-6 py-4">
          <p className="text-sm text-muted-foreground">
            {dueDate
              ? t("dataEntry.instruction", {
                  dueDate: toDisplayDueDate(dueDate).toLocaleDateString(i18n.language),
                })
              : t("dataEntry.instructionNoDueDate")}
          </p>
        </div>

        <div className="px-6 pb-6">
          <div className="overflow-hidden rounded-md border">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="w-36 px-3 py-2 text-left font-medium">{t("dataEntry.fieldTable.fieldTitle")}</th>
                  <th className="px-3 py-2 text-left font-medium">{t("dataEntry.fieldTable.currentValue")}</th>
                  <th className="px-3 py-2 text-left font-medium">{t("dataEntry.fieldTable.newValue")}</th>
                  <th className="w-24 px-3 py-2 text-center font-medium">{t("dataEntry.fieldTable.notPossible")}</th>
                  <th className="px-3 py-2 text-left font-medium">{t("dataEntry.fieldTable.comment")}</th>
                </tr>
              </thead>
              <tbody>
                {tableFieldList.map((field) => (
                  <DataEntryFieldRow
                    key={field.key}
                    fieldKey={field.key}
                    labelKey={field.labelKey}
                    fieldType={field.fieldType}
                    workspaceId={workspaceId}
                    currentValue={fieldValues ? getDisplayValue(fieldValues, field.key, i18n.language) : null}
                    defaultValue={fieldValues ? getRawValue(fieldValues, field.key) : null}
                    rawDescription={
                      (field.key === "description" || field.key === "indicatorDescription") && fieldValues
                        ? getRawValue(fieldValues, field.key)
                        : undefined
                    }
                    savedResponse={fieldResponseMap[field.key] ?? null}
                    draft={draftMap[field.key] ?? null}
                    onSave={handleSaveField}
                    disabled={isReadOnly}
                    minDate={field.key === "endDate" ? minSelectableEndDate : undefined}
                    maxDate={field.key === "startDate" ? maxSelectableStartDate : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {showValuesEditor && fieldValues && (
            <div className="mt-4">
              <IndicatorValuesField
                dataLineList={fieldValues.dataLineList ?? []}
                currentValues={fieldValues.values ?? []}
                savedResponse={fieldResponseMap["values"] ?? null}
                onSave={onSaveField}
                disabled={isReadOnly}
              />
            </div>
          )}
        </div>

        {documentsRequested && dataRequestEntityId && (
          <DataRequestDocumentsSection
            ownerId={dataRequestEntityId}
            isEditing={!isReadOnly}
            label={t("dataEntry.documents")}
            className="px-6 pb-6"
          />
        )}

        {freeTextQuestions.length > 0 && (
          <FreeTextSection
            key={dataRequestEntityId}
            questions={freeTextQuestions}
            initialAnswers={initialFreeTextAnswers}
            isReadOnly={isReadOnly}
            onSave={onSaveFreeText}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end border-t px-6 py-4">
        <Button onClick={onSubmit} disabled={isReadOnly || submitting}>
          {t("dataEntry.submit")}
        </Button>
      </div>
    </div>
  );
}
